import Box from '@mui/material/Box';
import { CompanyRole } from 'tdc-web-backend/enums/enums';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import MultiStep from '../../../components/multisteps/Multistep';
import useMultiStep from '../../../components/multisteps/useMultiStep';
import User from './steps/User';
import Overview from './steps/Overview';
import Admin from './steps/Admin';
import { printHelperTextForSpecificCurrentStep } from '../../../utils/helpers';
import { helperTexts, initialSellerState } from './utils';
import { TPostRegistration, TUserType } from './interface';
import useCreate from '../../../utils/hooks/crud-hooks/useCreate';
import DetailsSeller from './steps/DetailsSeller';
import DetailsBuyer from './steps/DetailsBuyer';
import { DASHBOARD_WRAPPER_PADDING } from '../../Dashboard/Main';
import useAuth from '../../../utils/hooks/useAuth';
import RegistrationAssistantSidebar from './components/RegistrationAssistantSidebar';
import axios from 'axios';
import { useGetUserSessions } from './hooks/useGetUserSessions';

export const labels = ['User', 'Overview', 'Admin', 'Details'];

const PostRegistration = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { mutate: create, isLoading: isCreateLoading } = useCreate({ resource: 'companies' });
  const [isDataSubmitting, setIsDataSubmitting] = useState(false);

  const navigate = useNavigate();
  const {
    authData: { userData: user },
    updateUser,
    isLoadingUpdateUser,
  } = useAuth();

  const membership = user?.membership;

  const { currentStep, steps, onStepChange, form } = useMultiStep<TPostRegistration>({
    labels,
    initialState: initialSellerState,
  });

  const userType: TUserType = form.watch('user');
  const aiAssistanceEnabled = form.watch('aiAssistanceEnabled');
  const showSidebar = aiAssistanceEnabled && currentStep > 1;

  const detailsForUser =
    userType === CompanyRole.SellerService ? <DetailsSeller /> : <DetailsBuyer />;

  const { getSessions, isLoading: gettingSession } = useGetUserSessions();
  const [sessions, setSessions] = useState<any[]>([]);

  const getUserSessions = async () => {
    const resp = await getSessions();
    setSessions(resp);
  };

  const handleSessionCreated = (newSession: any) => {
    setSessions((prev) => [...prev, newSession]);
  };

  useEffect(() => {
    getUserSessions();
  }, []);

  useEffect(() => {
    // if a user HAS company tied to him, don't let them get to create-company
    if (membership !== null && membership !== undefined) navigate('/dashboard');
  }, [membership, navigate]);

  const onSubmit = (data: any) => {
    setIsLoading(true);
    const { tmpClients, ...companyDetails } = data.details;
    const { clients } = companyDetails;

    let clientsIds = [] as string[];
    if (userType === CompanyRole.SellerService)
      clientsIds = clients?.map(({ id }: { id: string }) => id);

    // updateUser({ jobTitle: data.admin.jobTitle });

    create(
      {
        ...companyDetails,
        ...(userType === CompanyRole.SellerService && { clients: clientsIds }),
        ...data.overview,
        roles: [data.user],
      },
      {
        onSuccess: () => {
          updateUser(
            { jobTitle: data.admin.jobTitle },
            {
              onSuccess: async () => {
                try {
                  // create AI user in microservice (non-blocking for registration)
                  await axios.post(
                    '/ai/users',
                    {
                      username: `${user?.firstName ?? 'user'}_${
                        user?.lastName ?? ''
                      }`.toLowerCase(),
                      external_id: user?.id,
                    },
                    {
                      headers: {
                        'Content-Type': 'application/json',
                        'X-User-ID': String(user?.id),
                      },
                    },
                  );
                } catch (err) {
                  // log and continue — do not block company creation flow
                  // eslint-disable-next-line no-console
                  console.error('Failed to create AI user:', err);
                } finally {
                  setIsLoading(false);
                }
              },
              onError: () => {
                setIsLoading(false);
              },
            },
          );
        },
        onError: () => {
          setIsLoading(false);
        },
      },
    );
  };
  return (
    <>
      <Box sx={{ width: '70%', p: `${DASHBOARD_WRAPPER_PADDING}px` }}>
        <MultiStep
          form={form}
          steps={steps}
          currentStep={currentStep}
          onChange={onStepChange}
          onSubmit={onSubmit}
          helperText={printHelperTextForSpecificCurrentStep(currentStep, helperTexts)}
          disableCancelButton
          isSubmitLoading={isCreateLoading || isLoadingUpdateUser || isDataSubmitting}
        >
          <>
            {currentStep === 1 && (
              <User
                setDataSubmitting={setIsDataSubmitting}
                sessions={sessions}
                onSessionCreated={handleSessionCreated}
              />
            )}
            {currentStep === 2 && <Overview />}
            {currentStep === 3 && <Admin />}
            {currentStep === 4 && detailsForUser}
          </>
        </MultiStep>
      </Box>

      {showSidebar && (
        <RegistrationAssistantSidebar
          currentStep={currentStep}
          userType={userType}
          sessions={sessions}
        />
      )}
    </>
  );
};

export default PostRegistration;
